import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useRecoilState, useRecoilValue} from "recoil";
import iconSend from '../../images/space/icon-send.png'
import tabState from "../state/ui/activeTabState";
import useFetchChat from "../hooks/useLoadChat";
import {
  connectedState, firebaseClaimsState,
  firebaseOrganisationsState,
  firebaseSessionIdState,
  firebaseUidState
} from "../state/firebaseState";
import useKeyboard from "../hooks/ui/useKeyboard";
import {toast} from "react-toastify";
import { Avatar } from "../components/Avatar";
import { useTranslation } from "react-i18next";
import {useActiveQuestion, useQuestionHasAnswered, useQuestionSecondsLeft} from "../hooks/useQuestions";
import {useDeleteMessage} from "../hooks/useFetchQuestions";
import boothModeState from "../state/ui/boothModeState";

const ChatMessage = ({item, isMe = false, canDelete, deleteLoading, onDelete}) => {
  return (
    <div className={`flex-row flex px-4 py-3 ${isMe ? 'flex-row-reverse' : ''}`}>

      <Avatar
        initial={item.avatar}
        character={item.name && item.name.split(" ").map((n)=>n[0]).slice(0,1).join("")}
        containerStyle={`w-8 h-8 lg:w-10 lg:h-10 ${isMe ? 'ml-3' : 'mr-3'} mt-1`}
        user={{
          id: item.userId,
        }}
      />

      <div className="flex-1-1 overflow-hidden">
        <p className={`text-left text-2xs uppercase font-medium tracking-wider py-1 ellipsis ${isMe ? 'text-gray-300 text-right ml-auto' : (item?.isOrganiser ? 'font-bold text-yellow-500' : 'text-purple-400')}`}>
          {item?.isOrganiser && <span className="p-0.5 text-white bg-yellow-500 rounded-full mr-1"><i className="fas fa-star fa-fw"></i></span> }
          {item.name}
        </p>
        <div className={`${isMe ? 'text-right flex-row-reverse' : ''} flex flex-row`}>
          <div
            className={`px-3 py-2 rounded-xl inline-block ${isMe ? 'bg-blue-300 bg-opacity-30' : 'bg-white bg-opacity-10'} ${item.deletedBy ? 'opacity-30' : ''}`}>
            <p
              className={`text-left md:text-sm text-base lg:text-sm dont-break-out ${isMe ? 'text-white font-semibold' : 'text-gray-200'}`}>
              {item.message}
            </p>
          </div>
          {canDelete && (
            <button className={`text-xs ${item.deletedBy > 0 ? 'text-blue-500': 'text-red-500'} ${isMe ? 'mr-2' : 'ml-2'}`} disabled={deleteLoading} onClick={() => {
              if(deleteLoading) {
                return;
              }

              if(window.confirm(`${item.deletedBy ? 'Undelete' : 'Delete'} this message by ${item.name}`)) {
                onDelete(!!item.deletedBy);
                toast(`Message has been ${item.deletedBy ? 'undeleted' : 'deleted'}`);
              }
            }}><i className={`fas ${item.deletedBy > 0 ? 'fa-undo': 'fa-times'}`}></i></button>
          )}
        </div>

      </div>
    </div>
  );
}

const QuestionMessage = ({wrapperClassName, item, inline = true, active = null}) => {
  const secondsLeft = useQuestionSecondsLeft(item);
  const {value, loading, setAnswer} = useQuestionHasAnswered(item);
  const {t} = useTranslation('booth')

  if(!inline && secondsLeft <= 0) {
    return null;
  }

  // used to be active && secondsLeft > 0
  if(inline && item.question?.type !== 'inline') {
    return null;
  }


  const choices = Object.keys(item.question.choices || {}).map((k) => {
    return {
      id: k,
      ...(item?.question?.choices?.[k] || {})
    }
  })

  const disabled = value || loading || secondsLeft <= 0;

  return (
    <div className={`${wrapperClassName} w-full relative z-10`}>
      <div className="relative z-10">
        <div className={`px-3 py-2 text-sm text-center font-bold ${disabled ? "bg-gray-700 text-gray-200" : "bg-yellow-600 text-white"} rounded-t-lg`}>
          {value ? t('thank-you-for-voting') : t('please-select-an-option')}

        </div>
        <div className={`px-3 pt-2 pb-3 flex flex-col space-y-3 text-center text-gray-900 ${disabled ? "bg-gray-400" : "bg-gray-50"} relative overflow-hidden rounded-b-lg overflow-hidden shadow-2xl`}>
          <p className="font-semibold opacity-90">
            {item.question.label}
          </p>
          <div className="flex flex-col w-full space-y-2">

            {choices.map(choice => (
              <button
                className={`
                relative z-10 flex items-center justify-center py-2 font-bold border rounded-lg cursor-pointer focus:outline-none ease-in duration-100
                ${value === choice.id ? 'bg-green-100 border-green-400' : 'bg-gray-200 border-gray-500 border-opacity-30 bg-opacity-0'}
                ${disabled ? 'cursor-not-allowed text-gray-500 filter grayscale brightness-75' : 'hover:border-opacity-80 hover:bg-opacity-40'}
              `}
                onClick={() => setAnswer(choice.id)}
                disabled={disabled}
                key={"choice"+choice.id}
              >
                {choice.label}
              </button>
            ))}
          </div>
          <p className="text-xs opacity-80">{secondsLeft > 0 ? `${t('poll-ends-in', { seconds: secondsLeft })}` : t('poll-has-ended')}</p>
        </div>
      </div>
    </div>
  );
}

export default function Chat() {
  const {t} = useTranslation('lounge');
  const containerRef = useRef();
  const activeTab = useRecoilValue(tabState);

  const firebaseUid = useRecoilValue(firebaseUidState);
  const firebaseSessionId = useRecoilValue(firebaseSessionIdState);

  const {
    status,
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    sendMessage,
    resetChat,
  } = useFetchChat();

  const canFetchMore = !isFetching && !isFetchingNextPage && hasNextPage;

  const resetChatTimerRef = useRef();

  const activeQuestionMessage = useActiveQuestion();

  const firebaseClaims = useRecoilValue(firebaseClaimsState);
  const {loading: deleteLoading, mutate: deleteMessage} = useDeleteMessage();

  const boothMode = useRecoilValue(boothModeState);

  useLayoutEffect(() => {
    if(activeQuestionMessage?.question?.layout === 'takeover') {
      return;
    }

    const fetch = e => {
      if(e.target.scrollTop <= (e.target.clientHeight - e.target.scrollHeight) * .95 && canFetchMore) {
        fetchNextPage().then(() => {
          // Reset any previous timers...
          clearInterval(resetChatTimerRef.current);

          // Here we try to reset the chat, only if the user
          // is scrolled all the way to the bottom
          resetChatTimerRef.current = setInterval(() => {
            if(e.target.scrollTop === 0) {
              resetChat();

              clearInterval(resetChatTimerRef.current);
            }
          }, 10 * 1000);
        })
      }
    };

    containerRef.current && containerRef.current.addEventListener('scroll', fetch);
    return () => {
      containerRef.current && containerRef.current.removeEventListener('scroll', fetch);
      clearInterval(resetChatTimerRef.current);
    }
  }, [canFetchMore, activeQuestionMessage?.question?.layout]);

  return (
    <div className={`tab-view chat ${activeTab !== 2 ? 'hidden user-select-none' : ''}`}>
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-gray-400 border-opacity-25 flex flex-row justify-between">
          <div className="flex flex-row items-center">
            <span className={`w-2 h-2 rounded-full bg-red-600 inline-block ${!firebaseSessionId ? 'animate-ping' : ''}`} />
            <h1 className="text-white text-left ml-2">{t('live_chat')}</h1>
          </div>
        </div>

        {activeQuestionMessage?.question?.layout === 'takeover' ? (
          <QuestionMessage item={activeQuestionMessage} inline={false} active={true} wrapperClassName={'px-4 py-3'} />
        ) : (
          <>
            {activeQuestionMessage?.question?.layout === 'pinned' && (
              <QuestionMessage item={activeQuestionMessage} inline={false} active={true} wrapperClassName={'pt-3 pl-4 pr-6 -mb-4 w-full'}/>
            )}
            <div ref={containerRef} className="relative z-0 pt-6 chat-history overflow-y-scroll custom-scrollbar flex flex-col-reverse" style={{overflowAnchor: 'none'}}>
              <div className="flex-grow">
                {data?.pages && data?.pages.map((items, index) => {
                  return items.map(item => {
                    if(!boothMode && item.deletedBy > 0) {
                      return null;
                    }
                    else if(item.type === 'question') {
                      if(item.deletedBy > 0) {
                        return null
                      }

                      return <QuestionMessage key={`${item.id}-${index}`} item={item} isMe={item.userId === firebaseUid} inline={true} active={activeQuestionMessage?.id === item.id} wrapperClassName={'px-4 py-3'} />;
                    }
                    else {
                      if(item.deletedBy > 0 && !(firebaseClaims?.isAdmin || firebaseClaims?.isOrganiser)) {
                        return null
                      }

                      return <ChatMessage
                        key={`${item.id}-${index}`}
                        item={item}
                        isMe={item.userId === firebaseUid}
                        canDelete={boothMode && (firebaseClaims?.isAdmin || firebaseClaims?.isOrganiser)}
                        onDelete={(undo = false) => {
                          if (deleteLoading) {
                            return;
                          }
                          deleteMessage(item.id, undo);
                        }}
                        deleteLoading={deleteLoading}
                      />;
                    }
                  })
                })}
              </div>
            </div>
            <ChatInput disabled={!firebaseSessionId} containerRef={containerRef} sendMessage={sendMessage} />
          </>
        )}

      </div>

    </div>
  )
}

const ChatInput = ({disabled, containerRef, sendMessage}) => {
  const {keyboard, onFocus, onBlur} = useKeyboard();
  const {t} = useTranslation('lounge');

  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const submitMessage = (e) => {
    e.preventDefault();
    if(disabled || isSending) {
      return;
    }
    if(!message){
      toast("Your message needs to be longer than 1 character", {type: 'error'});
      return;
    }
    setIsSending(true);
    sendMessage(message).then(() => {
      containerRef.current.scrollTop = 0;
      setIsSending(false);
    })
    setMessage('');
  }

  return (
    <div className="px-4 py-3 my-auto h-14 flex items-center tab-view-inset-padding">
      <div className="flex-row flex flex-1 md:mb-0">
        <input
          key="chatInput"
          type="text"
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={e => setMessage(e.target.value)}
          value={message}
          className="bg-white bg-opacity-10 rounded-full w-full px-4 py-2 h-10 md:py-3 text-white"
          placeholder={t('say_something')}
          disabled={disabled || isSending}
          onKeyDown={e => {
            if(e.keyCode === 13 && !e.shiftKey && !isSending) {
              submitMessage(e);
            }
          }}
        />
        <button onClick={submitMessage} disabled={isSending} className="bg-blue-400 p-3 rounded-full ml-3 text-white w-20 h-10 hover:bg-blue-500">
          <img src={iconSend} className="w-full h-full object-contain" draggable="false"></img>
        </button>
      </div>
    </div>
  )
}
